import React, { useState, useEffect, useMemo, useRef } from 'react';
import { db, collection, doc, getDoc, getDocs, updateDoc } from '../firebase';
import { useTeam } from '../contexts/TeamContext';
import { useTeamPath } from '../hooks/useTeamPath';
import { useCanEditTeam } from '../hooks/useIsTeamOwner';
import { useParams, useHistory } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { buildRoster } from '../utils/roster';
import '../App.css';

const balanceTeams = (players) => {
  const sorted = [...players].sort((a, b) => Number(b.mvpPoints || 0) - Number(a.mvpPoints || 0));
  const pattern = 'abbaabbaabbaabba';
  const teamA = [];
  const teamB = [];
  sorted.forEach((player, i) => {
    if (pattern[i % pattern.length] === 'a') teamA.push(player.name);
    else teamB.push(player.name);
  });
  return { teamA, teamB };
};

const AutoAssign = ({ roster, setTeamA, setTeamB, setStats }) => {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const togglePlayer = (name) => {
    setSelected(prev =>
      prev.includes(name) ? prev.filter(p => p !== name) : [...prev, name]
    );
  };

  const handleAssign = () => {
    if (selected.length < 2) return;
    const selectedPlayers = roster.filter(p => selected.includes(p.name));
    const { teamA: newA, teamB: newB } = balanceTeams(selectedPlayers);
    setStats({});
    setTeamA(newA);
    setTeamB(newB);
    setSelected([]);
    setOpen(false);
  };

  return (
    <div style={{ marginTop: 16 }} ref={dropdownRef}>
      <label style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-3)', fontWeight: 600, display: 'block', marginBottom: 8 }}>
        Automatski rasporedi
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <button
            type="button"
            onClick={() => setOpen(o => !o)}
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
              background: 'var(--surface-2, #1e2533)',
              border: '1px solid var(--border, #2a3442)',
              borderRadius: 8,
              color: 'var(--text-1, #f1f5f9)',
              cursor: 'pointer',
              fontSize: '0.9rem',
            }}
          >
            <span>
              {selected.length > 0
                ? `${selected.length} igrača odabrano`
                : 'Odaberi igrače za raspoređivanje'}
            </span>
            <span style={{ opacity: 0.6, fontSize: '0.75rem' }}>{open ? '▲' : '▼'}</span>
          </button>
          {open && (
            <div style={{
              position: 'absolute',
              zIndex: 100,
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: 'var(--surface-2, #1e2533)',
              border: '1px solid var(--border, #2a3442)',
              borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
              maxHeight: 280,
              overflowY: 'auto',
            }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 14px', cursor: 'pointer',
                borderBottom: '1px solid var(--border, #2a3442)',
                fontSize: '0.83rem', color: 'var(--text-3)',
                fontWeight: 600,
              }}>
                <input
                  type="checkbox"
                  checked={selected.length === roster.length}
                  onChange={() => setSelected(selected.length === roster.length ? [] : roster.map(p => p.name))}
                  style={{ width: 16, height: 16 }}
                />
                Odaberi sve
              </label>
              {roster.map((player) => (
                <label
                  key={player.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 14px', cursor: 'pointer',
                    borderBottom: '1px solid var(--border, #2a3442)',
                    fontSize: '0.88rem', color: 'var(--text-1, #f1f5f9)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.includes(player.name)}
                    onChange={() => togglePlayer(player.name)}
                    style={{ accentColor: '#a78bfa', width: 16, height: 16 }}
                  />
                  {player.name}
                </label>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleAssign}
          disabled={selected.length < 2}
          className="btn btn-primary"
          style={{ whiteSpace: 'nowrap', opacity: selected.length < 2 ? 0.5 : 1 }}
        >
          Rasporedi
        </button>
      </div>
      {selected.length > 0 && (
        <p style={{ fontSize: '0.78rem', color: 'var(--text-3)', marginTop: 6 }}>
          Raspoređuje prema MVP bodovima (snake draft) · {selected.length} igrača
        </p>
      )}
    </div>
  );
};

const TeamMultiSelect = ({ roster, teamA, teamB, setTeamA, setTeamB, setStats }) => {
  const [openTeam, setOpenTeam] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenTeam(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggle = (team, playerName) => {
    const list = team === 'A' ? teamA : teamB;
    const setList = team === 'A' ? setTeamA : setTeamB;
    if (list.includes(playerName)) {
      setList(list.filter(p => p !== playerName));
      setStats(prev => { const next = { ...prev }; delete next[playerName]; return next; });
    } else {
      setList([...list, playerName]);
    }
  };

  return (
    <div className="dual-select" ref={dropdownRef}>
        {['A', 'B'].map((team) => {
          const teamList = team === 'A' ? teamA : teamB;
          const otherList = team === 'A' ? teamB : teamA;
          const isOpen = openTeam === team;
          return (
            <div key={team} style={{ position: 'relative' }}>
              <label>Tim {team}</label>
              <button
                type="button"
                onClick={() => setOpenTeam(isOpen ? null : team)}
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  background: 'var(--surface-2, #1e2533)',
                  border: '1px solid var(--border, #2a3442)',
                  borderRadius: 8,
                  color: 'var(--text-1, #f1f5f9)',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                }}
              >
                <span>
                  {teamList.length > 0
                    ? teamList.join(', ')
                    : 'Odaberi igraca'}
                </span>
                <span style={{ marginLeft: 8, opacity: 0.6, fontSize: '0.75rem' }}>{isOpen ? '▲' : '▼'}</span>
              </button>
              {isOpen && (
                <div style={{
                  position: 'absolute',
                  zIndex: 100,
                  top: 'calc(100% + 4px)',
                  left: 0,
                  right: 0,
                  background: 'var(--surface-2, #1e2533)',
                  border: '1px solid var(--border, #2a3442)',
                  borderRadius: 8,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                  maxHeight: 280,
                  overflowY: 'auto',
                }}>
                  {roster.map((player) => {
                    const inThis = teamList.includes(player.name);
                    const inOther = otherList.includes(player.name);
                    return (
                      <label
                        key={player.key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '8px 14px',
                          cursor: inOther ? 'not-allowed' : 'pointer',
                          opacity: inOther ? 0.4 : 1,
                          borderBottom: '1px solid var(--border, #2a3442)',
                          fontSize: '0.88rem',
                          color: 'var(--text-1, #f1f5f9)',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={inThis}
                          disabled={inOther}
                          onChange={() => toggle(team, player.name)}
                          style={{ accentColor: team === 'A' ? '#00f5a0' : '#38bdf8', width: 16, height: 16 }}
                        />
                        {player.name}
                        {inOther && (
                          <span style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-3)' }}>
                            Tim {team === 'A' ? 'B' : 'A'}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
  );
};

const MatchDetails = ({ user }) => {
  const { teamId, loading: teamLoading } = useTeam();
  const tp = useTeamPath();
  const isTeamOwner = useCanEditTeam(user);
  const { id } = useParams();
  const history = useHistory();
  const [match, setMatch] = useState(null);
  const [roster, setRoster] = useState([]);
  const [teamA, setTeamA] = useState([]);
  const [teamB, setTeamB] = useState([]);
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [stats, setStats] = useState({});

  useEffect(() => {
    if (!teamId) return;

    const fetchMatch = async () => {
      try {
        const matchRef = doc(db, 'teams', teamId, 'matches', id);
        const matchSnap = await getDoc(matchRef);
        if (!matchSnap.exists()) {
          history.push(tp('/schedule'));
          return;
        }

        const matchData = matchSnap.data();
        setMatch({ id: matchSnap.id, ...matchData });
        setTeamA(matchData.teamA || []);
        setTeamB(matchData.teamB || []);
        setScoreA(matchData.scoreA || 0);
        setScoreB(matchData.scoreB || 0);
        setStats(matchData.stats || {});
      } catch (error) {
        console.error('Greska pri dohvacanju utakmice:', error);
      }
    };

    const fetchRoster = async () => {
      try {
        const [playersSnapshot, membersSnapshot] = await Promise.all([
          getDocs(collection(db, 'teams', teamId, 'players')),
          getDocs(collection(db, 'teams', teamId, 'members')),
        ]);

        setRoster(
          buildRoster(
            playersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
            membersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
          )
        );
      } catch (error) {
        console.error('Greska pri dohvacanju igraca:', error);
      }
    };

    fetchMatch();
    fetchRoster();
  }, [history, id, teamId, tp]);

  const statsData = useMemo(
    () =>
      Object.keys(stats || {}).map((player) => ({
        name: player,
        goals: stats[player]?.goals || 0,
        assists: stats[player]?.assists || 0,
      })),
    [stats]
  );

  const getPlayerStat = (name) => ({
    name,
    goals: Number(stats?.[name]?.goals || 0),
    assists: Number(stats?.[name]?.assists || 0),
  });

  const sortByContribution = (a, b) => {
    const aTotal = a.goals + a.assists;
    const bTotal = b.goals + b.assists;
    if (bTotal !== aTotal) return bTotal - aTotal;
    if (b.goals !== a.goals) return b.goals - a.goals;
    return a.name.localeCompare(b.name);
  };

  const teamAStats = useMemo(
    () => teamA.map(getPlayerStat).sort(sortByContribution),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamA, stats]
  );

  const teamBStats = useMemo(
    () => teamB.map(getPlayerStat).sort(sortByContribution),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [teamB, stats]
  );

  const mvp = useMemo(() => {
    const all = [...teamAStats, ...teamBStats].filter((p) => p.goals > 0 || p.assists > 0);
    return all.length > 0 ? all.sort(sortByContribution)[0] : null;
  }, [teamAStats, teamBStats]);

  const sumStats = (list) =>
    list.reduce(
      (acc, p) => ({ goals: acc.goals + p.goals, assists: acc.assists + p.assists }),
      { goals: 0, assists: 0 }
    );

  const totalsA = useMemo(() => sumStats(teamAStats), [teamAStats]);
  const totalsB = useMemo(() => sumStats(teamBStats), [teamBStats]);

  const maxYTick = useMemo(() => {
    const max = statsData.reduce(
      (m, p) => Math.max(m, p.goals || 0, p.assists || 0),
      0
    );
    return Math.max(1, Math.ceil(max));
  }, [statsData]);

  const updateRosterEntryTotals = async (entry, delta) => {
    if (!teamId || !entry) return;
    const currentGoals = Number(entry.goals || 0);
    const currentAssists = Number(entry.assists || 0);
    const currentMatches = Number(entry.matchesPlayed || 0);

    const patch = {
      goals: Math.max(0, currentGoals + delta.goals),
      assists: Math.max(0, currentAssists + delta.assists),
      matchesPlayed: Math.max(0, currentMatches + delta.matchesPlayed),
    };

    const ref =
      entry.kind === 'member'
        ? doc(db, 'teams', teamId, 'members', entry.id)
        : doc(db, 'teams', teamId, 'players', entry.id);

    await updateDoc(ref, patch);
  };

  const handleSaveMatch = async () => {
    if (!teamId || !match) return;

    try {
      const previousTeamA = match.teamA || [];
      const previousTeamB = match.teamB || [];
      const previousStats = match.stats || {};
      const nextStats = stats || {};
      const matchRef = doc(db, 'teams', teamId, 'matches', id);

      await updateDoc(matchRef, {
        teamA,
        teamB,
        scoreA: Number(scoreA) || 0,
        scoreB: Number(scoreB) || 0,
        stats: nextStats,
      });

      const rosterByName = new Map(roster.map((entry) => [entry.name, entry]));
      const affectedNames = new Set([
        ...previousTeamA,
        ...previousTeamB,
        ...teamA,
        ...teamB,
        ...Object.keys(previousStats),
        ...Object.keys(nextStats),
      ]);

      for (const name of affectedNames) {
        const entry = rosterByName.get(name);
        if (!entry) continue;

        const oldPlayed = previousTeamA.includes(name) || previousTeamB.includes(name) ? 1 : 0;
        const newPlayed = teamA.includes(name) || teamB.includes(name) ? 1 : 0;
        const delta = {
          matchesPlayed: newPlayed - oldPlayed,
          goals: (nextStats[name]?.goals || 0) - (previousStats[name]?.goals || 0),
          assists: (nextStats[name]?.assists || 0) - (previousStats[name]?.assists || 0),
        };

        if (!delta.matchesPlayed && !delta.goals && !delta.assists) continue;
        await updateRosterEntryTotals(entry, delta);
      }

      setMatch({
        ...match,
        teamA,
        teamB,
        scoreA: Number(scoreA) || 0,
        scoreB: Number(scoreB) || 0,
        stats: nextStats,
      });

      setRoster((prev) =>
        prev.map((entry) => {
          const oldPlayed = previousTeamA.includes(entry.name) || previousTeamB.includes(entry.name) ? 1 : 0;
          const newPlayed = teamA.includes(entry.name) || teamB.includes(entry.name) ? 1 : 0;
          const deltaGoals =
            (nextStats[entry.name]?.goals || 0) - (previousStats[entry.name]?.goals || 0);
          const deltaAssists =
            (nextStats[entry.name]?.assists || 0) - (previousStats[entry.name]?.assists || 0);
          const deltaMatches = newPlayed - oldPlayed;

          if (!deltaGoals && !deltaAssists && !deltaMatches) return entry;
          return {
            ...entry,
            goals: Math.max(0, Number(entry.goals || 0) + deltaGoals),
            assists: Math.max(0, Number(entry.assists || 0) + deltaAssists),
            matchesPlayed: Math.max(0, Number(entry.matchesPlayed || 0) + deltaMatches),
          };
        })
      );

      alert('Utakmica i statistika igraca su azurirani.');
    } catch (error) {
      console.error('Greska pri spremanju utakmice:', error);
    }
  };

  if (teamLoading) {
    return <div className="loading-screen">Ucitavanje tima...</div>;
  }

  if (!match) {
    return <div className="empty-state" style={{ margin: 40 }}>Ucitavanje podataka...</div>;
  }

  return (
    <div className="match-details">
      <section className="match-hero">
        <div className="match-hero-meta">
          <span>{match.date}</span>
          <span>{match.time}</span>
          <span>{match.location}</span>
        </div>
        <div className="match-hero-score">
          <div className="match-hero-team">
            <span className="team-label">Tim A</span>
            <div className="team-players-list">{teamA.length > 0 ? teamA.join(', ') : '—'}</div>
          </div>
          <div className="match-hero-score-number">
            <span>{scoreA}</span>
            <span className="divider">:</span>
            <span>{scoreB}</span>
          </div>
          <div className="match-hero-team right">
            <span className="team-label">Tim B</span>
            <div className="team-players-list">{teamB.length > 0 ? teamB.join(', ') : '—'}</div>
          </div>
        </div>
      </section>

      {isTeamOwner && (
        <>
          <section className="match-section">
            <h3>Dodaj igrace u timove</h3>
            <TeamMultiSelect
              roster={roster}
              teamA={teamA}
              teamB={teamB}
              setTeamA={setTeamA}
              setTeamB={setTeamB}
              setStats={setStats}
            />
            <AutoAssign
              roster={roster}
              setTeamA={setTeamA}
              setTeamB={setTeamB}
              setStats={setStats}
            />
          </section>

          <section className="match-section">
            <h3>Rezultat</h3>
            <div className="score-row">
              <input type="number" value={scoreA} onChange={(e) => setScoreA(Number(e.target.value) || 0)} onFocus={(e) => e.target.select()} />
              <span className="divider">:</span>
              <input type="number" value={scoreB} onChange={(e) => setScoreB(Number(e.target.value) || 0)} onFocus={(e) => e.target.select()} />
            </div>
          </section>

          <section className="match-section">
            <h3>Statistika igraca</h3>
            {[...teamA, ...teamB]
              .sort((a, b) => a.localeCompare(b))
              .map((playerName) => (
                <div key={playerName} className="stat-entry-grid">
                  <span className="stat-player-name">{playerName}</span>
                  <div>
                    <label>Golovi</label>
                    <input
                      type="number"
                      value={stats[playerName]?.goals || 0}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setStats({
                          ...stats,
                          [playerName]: {
                            ...stats[playerName],
                            goals: Number(e.target.value) || 0,
                          },
                        })
                      }
                    />
                  </div>
                  <div>
                    <label>Asistencije</label>
                    <input
                      type="number"
                      value={stats[playerName]?.assists || 0}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) =>
                        setStats({
                          ...stats,
                          [playerName]: {
                            ...stats[playerName],
                            assists: Number(e.target.value) || 0,
                          },
                        })
                      }
                    />
                  </div>
                </div>
              ))}

            <button onClick={handleSaveMatch} className="btn btn-primary btn-lg" style={{ marginTop: 16 }}>
              Spremi utakmicu
            </button>
          </section>
        </>
      )}

      <section className="match-section">
        <h3>Statistika utakmice</h3>
        {(teamAStats.length > 0 || teamBStats.length > 0) ? (
          <div className="match-stats">
            {mvp && (
              <div className="match-mvp">
                <span className="match-mvp-badge"><span role="img" aria-label="zvijezda">⭐</span> Igrač utakmice</span>
                <div className="match-mvp-name">{mvp.name}</div>
                <div className="match-mvp-stats">
                  <span className="stat-pill stat-pill-goal">
                    <span className="stat-pill-icon" role="img" aria-label="golovi">⚽</span>
                    <span className="stat-pill-value">{mvp.goals}</span>
                    <span className="stat-pill-label">golova</span>
                  </span>
                  <span className="stat-pill stat-pill-assist">
                    <span className="stat-pill-icon" role="img" aria-label="asistencije">🎯</span>
                    <span className="stat-pill-value">{mvp.assists}</span>
                    <span className="stat-pill-label">asistencija</span>
                  </span>
                </div>
              </div>
            )}

            <div className="match-stats-teams">
              {[
                { key: 'A', label: 'Tim A', players: teamAStats, totals: totalsA, score: scoreA },
                { key: 'B', label: 'Tim B', players: teamBStats, totals: totalsB, score: scoreB },
              ].map((column) => (
                <div key={column.key} className="match-stats-team">
                  <div className="match-stats-team-head">
                    <span className="match-stats-team-label">{column.label}</span>
                    <span className="match-stats-team-score">{column.score}</span>
                  </div>
                  {column.players.length > 0 ? (
                    <ul className="match-stats-list">
                      {column.players.map((player) => (
                        <li key={`${column.key}-${player.name}`} className="match-stats-row">
                          <span className="match-stats-player">
                            {mvp && player.name === mvp.name && (
                              <span className="match-stats-crown" role="img" aria-label="MVP">👑</span>
                            )}
                            {player.name}
                          </span>
                          <span className="match-stats-badges">
                            <span
                              className={`stat-chip ${player.goals > 0 ? 'stat-chip-goal' : 'stat-chip-muted'}`}
                              title="Golovi"
                            >
                              <span role="img" aria-label="golovi">⚽</span> {player.goals}
                            </span>
                            <span
                              className={`stat-chip ${player.assists > 0 ? 'stat-chip-assist' : 'stat-chip-muted'}`}
                              title="Asistencije"
                            >
                              <span role="img" aria-label="asistencije">🎯</span> {player.assists}
                            </span>
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="empty-inline" style={{ marginTop: 8 }}>Nema igrača.</div>
                  )}
                  <div className="match-stats-total">
                    <span>Ukupno</span>
                    <span className="match-stats-total-values">
                      <span><span role="img" aria-label="golovi">⚽</span> {column.totals.goals}</span>
                      <span><span role="img" aria-label="asistencije">🎯</span> {column.totals.assists}</span>
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {statsData.length > 0 && (
              <details className="match-stats-chart">
                <summary>Prikaži grafikon</summary>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={statsData} margin={{ top: 16, right: 8, left: -16, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                    <YAxis
                      allowDecimals={false}
                      domain={[0, maxYTick]}
                      tick={{ fill: '#94a3b8', fontSize: 12 }}
                    />
                    <Tooltip
                      cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                      contentStyle={{
                        background: '#0f1419',
                        border: '1px solid #2a3442',
                        borderRadius: 8,
                        color: '#f1f5f9',
                      }}
                    />
                    <Legend wrapperStyle={{ color: '#94a3b8' }} />
                    <Bar dataKey="goals" fill="#00f5a0" name="Golovi" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="assists" fill="#38bdf8" name="Asistencije" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </details>
            )}
          </div>
        ) : (
          <div className="empty-inline">Nema dostupnih podataka.</div>
        )}
      </section>
    </div>
  );
};

export default MatchDetails;
